import React, { useState, useEffect, useRef } from 'react';

interface TooltipProps {
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  targetId: string;
  isVisible: boolean;
  onNext: () => void;
  onSkip: () => void;
  currentStep: number;
  totalSteps: number;
  stepIndex: number;
}

const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  position, 
  targetId, 
  isVisible,
  onNext,
  onSkip,
  currentStep,
  totalSteps,
  stepIndex
}) => {
  const [highlightPosition, setHighlightPosition] = useState<React.CSSProperties>({
    top: '50%',
    right: '20px'
  });
  
  if (!isVisible) return null;

  // Use a portal to render the circle outside the main content area
  useEffect(() => {
    // Add temporary styles to adjust the info tooltip position if it exists
    const infoTooltip = document.querySelector('.info-tooltip') as HTMLElement | null;
    if (infoTooltip) {
      const originalBottom = infoTooltip.style.bottom;
      const originalZIndex = infoTooltip.style.zIndex;
      const originalTop = infoTooltip.style.top;
      const originalLeft = infoTooltip.style.left;
      const originalRight = infoTooltip.style.right;
      const originalPosition = infoTooltip.style.position;
      
      // Move it to the top left of the screen
      infoTooltip.style.position = 'fixed';
      infoTooltip.style.bottom = 'auto';
      infoTooltip.style.right = 'auto';
      infoTooltip.style.top = '10px';
      infoTooltip.style.left = '10px';
      infoTooltip.style.zIndex = '100002'; // Even higher than highlight indicators
      
      return () => {
        // Restore original position when unmounted
        infoTooltip.style.position = originalPosition;
        infoTooltip.style.bottom = originalBottom;
        infoTooltip.style.zIndex = originalZIndex;
        infoTooltip.style.top = originalTop;
        infoTooltip.style.left = originalLeft;
        infoTooltip.style.right = originalRight;
      };
    }
  }, []);

  // Attempt to directly target the Roster button if possible
  useEffect(() => {
    if (stepIndex === 1) {
      // Try to find the roster button in the mobile nav
      const navLinks = document.querySelectorAll('nav a');
      let rosterElement: HTMLElement | null = null;
      
      navLinks.forEach((link) => {
        if (link.textContent?.includes('Roster')) {
          rosterElement = link as HTMLElement;
        }
      });
      
      // If found, mark it for visibility
      if (rosterElement) {
        const originalPosition = rosterElement.style.position;
        const originalZIndex = rosterElement.style.zIndex;
        
        rosterElement.style.position = 'relative';
        rosterElement.style.zIndex = '200001'; // Extremely high z-index
        
        return () => {
          rosterElement!.style.position = originalPosition;
          rosterElement!.style.zIndex = originalZIndex;
        };
      }
    }
  }, [stepIndex]);

  // Update highlight position based on the targeted element
  useEffect(() => {
    updateHighlightPosition();
    
    // Add window resize listener to update positions
    window.addEventListener('resize', updateHighlightPosition);
    return () => {
      window.removeEventListener('resize', updateHighlightPosition);
    };
  }, [stepIndex]);
  
  const updateHighlightPosition = () => {
    let newPosition: React.CSSProperties = {};
    
    if (stepIndex === 0) {
      // New Team button (top-right)
      newPosition = { top: '120px', right: '43px' };
    } 
    else if (stepIndex === 1) {
      // Fixed position for the Roster icon in mobile nav
      newPosition = { bottom: '10px', left: '82px' };
    } 
    else if (stepIndex === 2) {
      // Practice creation
      newPosition = { bottom: '-40px', right: '42px' };
    } 
    else if (stepIndex === 3) {
      // Settings
      newPosition = { bottom: '-40px', right: '-37px' };
    } 
    else if (stepIndex === 4) {
      // Depth chart
      newPosition = { bottom: '-38px', left: '48%' };
    }
    
    setHighlightPosition(newPosition);
  };

  return (
    <>
      {/* Render the main tooltip with overlay */}
      <div className="fixed inset-0 z-[9999]" onClick={(e) => e.stopPropagation()}>
        {/* Semi-transparent overlay - cover entire screen */}
        <div className="absolute inset-0 bg-black bg-opacity-50" />
        
        {/* Special overlay just for the mobile nav */}
        <div 
          className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 z-[9999]" 
          style={{ pointerEvents: 'all', height: '5.3rem' }}
        />
        
        {/* Floating Highlight Indicator */}
        <div 
          className="fixed z-[100001] pointer-events-none"
          style={{
            ...highlightPosition,
            width: stepIndex === 0 ? '157px' : stepIndex === 1 ? '65px' : '80px',
            height: stepIndex === 0 ? '80px' : stepIndex === 1 ? '65px' : '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(61, 168, 180, 0.2)', 
            border: '3px solid #3da8b4',
            boxShadow: '0 0 20px 10px rgba(61, 168, 180, 0.5)',
            animation: 'pulse-highlight 1.5s infinite alternate'
          }}
        />
        
        {/* Tooltip container - centered in viewport */}
        <div
          className="fixed z-[10000] bg-[#2C3E50] text-white px-6 py-4 rounded-xl shadow-lg text-sm font-chalk"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '400px',
            width: '90%',
          }}
        >
          {/* Add global animation styles */}
          <style>
            {`
              @keyframes pulse-highlight {
                0% { 
                  transform: scale(0.9) ${stepIndex !== 1 ? 'translate(-50%, -50%)' : ''};
                  box-shadow: 0 0 20px 5px rgba(61, 168, 180, 0.5);
                }
                100% { 
                  transform: scale(1.1) ${stepIndex !== 1 ? 'translate(-50%, -50%)' : ''};
                  box-shadow: 0 0 30px 15px rgba(61, 168, 180, 0.8);
                }
              }
            `}
          </style>
          
          {/* Content */}
          <div className="mb-4">{content}</div>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center gap-2">
            <span className="text-white text-sm font-chalk">
              {currentStep + 1} of {totalSteps}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onSkip}
                className="btn-secondary text-sm px-4 py-2 font-chalk"
              >
                Skip
              </button>
              <button
                onClick={onNext}
                className="btn-primary text-sm px-4 py-2 font-chalk"
              >
                {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Add a floating help button component
const FloatingHelpButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <button 
      onClick={onClick}
      className="fixed top-2 left-2 z-[100002] bg-blue-500 text-white p-2 rounded-full shadow-lg"
      style={{ 
        width: '40px', 
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: 'bold'
      }}
    >
      ?
    </button>
  );
};

// Help Flyout component
const HelpFlyout: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const helpTips = [
    {
      title: "Creating a Team",
      content: "Click the 'New Team' button to create your team with sport, age level, and experience level."
    },
    {
      title: "Managing Your Roster",
      content: "Use the Roster icon in the bottom menu to add, edit, and organize your players."
    },
    {
      title: "Creating Practices",
      content: "Create a practice plan with date, theme, and duration, then generate a detailed plan."
    },
    {
      title: "Settings",
      content: "Manage your account settings, invite coaches, and customize preferences."
    },
    {
      title: "Depth Chart",
      content: "Create a depth chart to organize your team's positions and rotations."
    }
  ];

  return (
    <div className="fixed inset-0 z-[100001] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      <div 
        className="relative bg-white rounded-xl shadow-xl max-w-md w-[90%] max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-blue-500 text-white p-4 font-bold flex justify-between items-center">
          <h2>Hustli Help</h2>
          <button onClick={onClose} className="text-xl">&times;</button>
        </div>
        <div className="p-4">
          {helpTips.map((tip, index) => (
            <div key={index} className="mb-4 pb-4 border-b border-gray-200 last:border-0">
              <h3 className="font-bold text-lg mb-2">{tip.title}</h3>
              <p>{tip.content}</p>
            </div>
          ))}
          <div className="mt-6">
            <button 
              onClick={() => {
                onClose();
                localStorage.setItem('onboardingComplete', 'false');
                window.location.reload();
              }}
              className="bg-blue-500 text-white px-4 py-2 rounded w-full"
            >
              Restart Full Onboarding Tour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface OnboardingStep {
  id: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  targetId: string;
}

export const OnboardingTooltips: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [showHelpFlyout, setShowHelpFlyout] = useState(false);

  const onboardingSteps: OnboardingStep[] = [
    {
      id: 'team-setup',
      content: 'Your first step is to create your team. Click the New Team button in the top right to set up your team details, including sport, age level, and experience level.',
      position: 'bottom',
      targetId: 'new-team-button'
    },
    {
      id: 'roster-management',
      content: 'The Roster icon in the bottom menu is where you can manage/import your players.',
      position: 'bottom',
      targetId: 'roster-nav-icon'
    },
    {
      id: 'practice-creation',
      content: 'Time to create your first practice! Set the date, theme, and duration for your practice. Then click the Generate Plan button and watch your practice come to life!',
      position: 'bottom',
      targetId: 'new-practice-button'
    },
    {
      id: 'settings',
      content: 'In settings, you can invite other coaches to your team, customize your preferences, and manage your account.',
      position: 'left',
      targetId: 'settings-button'
    },
    {
      id: 'depth-chart',
      content: 'Finally, set up your depth chart to organize your team\'s positions and rotations.',
      position: 'top',
      targetId: 'depth-chart-button'
    }
  ];

  useEffect(() => {
    // Check if onboarding has been completed before
    const hasCompletedOnboarding = localStorage.getItem('onboardingComplete');
    if (hasCompletedOnboarding === 'true') {
      setIsOnboardingComplete(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsOnboardingComplete(true);
      localStorage.setItem('onboardingComplete', 'true');
    }
  };

  const handleSkip = () => {
    setIsOnboardingComplete(true);
    localStorage.setItem('onboardingComplete', 'true');
  };

  const handleHelpClick = () => {
    setShowHelpFlyout(true);
  };

  const handleCloseFlyout = () => {
    setShowHelpFlyout(false);
  };

  // IMPORTANT: Don't return null early - always render something
  const currentTooltip = onboardingSteps[currentStep];

  // Always render, conditionally show content based on state
  return (
    <>
      {!isOnboardingComplete ? (
        // Show tooltip during onboarding
        <Tooltip
          content={currentTooltip.content}
          position={currentTooltip.position}
          targetId={currentTooltip.targetId}
          isVisible={true}
          onNext={handleNext}
          onSkip={handleSkip}
          currentStep={currentStep}
          totalSteps={onboardingSteps.length}
          stepIndex={currentStep}
        />
      ) : null}
    </>
  );
};

export default OnboardingTooltips; 